/**
 * CommandPaletteModal — self-contained ⌘K / Ctrl+K command palette.
 *
 * Inlined from the (now-deleted) `hts-ui` repo's `@hts/command-palette` v0.1.0
 * on 2026-08-04. Source preserved verbatim so behavior matches what shipped
 * in production up to and including the v0.1.0 release. The upstream repo
 * is being retired; see PR #TBD for the migration rationale.
 *
 * Public API kept identical to v0.1.0 so the existing wrapper at
 * `CommandPalette.tsx` and any future consumer can swap the import path
 * without further changes.
 *
 * Theming is entirely via HTS design-token CSS variables (see the wrapper's
 * `SS_PALETTE_THEME` for the `--hts-*` ↔ hts-ui-var mapping). The component
 * ships zero runtime dependencies — only `react` and `react-dom` are peers.
 */

import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// Types (from hts-ui's src/types.ts)
// ---------------------------------------------------------------------------

/**
 * A single entry in the command palette.
 *
 * `keywords` is matched by the fuzzy filter but is not rendered. Use it to
 * surface alternate spellings, IDs, or domain synonyms ("ticket", "company",
 * etc.) without crowding the row UI.
 *
 * `hideWhenEmpty` is for items from very large lists (e.g. a project list
 * with thousands of rows) that should only appear once the user has typed
 * a query — keep the preloaded set small.
 */
export interface CommandItem {
  id: string;
  label: string;
  /** Secondary muted text on the right (e.g. section, shortcut, parent record). */
  hint?: string;
  /** Grouping header this item appears under. */
  group?: string;
  icon?: ReactNode;
  /** Extra text matched by the fuzzy filter but not shown. */
  keywords?: string;
  /** Hide from the list until the user types a query. */
  hideWhenEmpty?: boolean;
  perform: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  items: CommandItem[];
  placeholder?: string;
  /**
   * Fired whenever the search text changes (and reset to '' on open/close).
   * Lets the host debounce it into server-side entity search (tickets,
   * accounts, etc.) for result sets too large to preload. Fetched results
   * are appended to `items` and pass the same client-side fuzzy filter as
   * everything else.
   */
  onQueryChange?: (q: string) => void;
  /** Override the search-input leading icon. Defaults to an inline magnifier SVG. */
  searchIcon?: ReactNode;
  /** Override the icon shown on the active row (Enter hint). Defaults to an inline return-arrow SVG. */
  enterIcon?: ReactNode;
  /** Rendered when the query has no matches. Receives the current query. */
  renderEmpty?: (query: string) => ReactNode;
  /** Optional z-index override. Defaults to 2000. */
  zIndex?: number;
}

// ---------------------------------------------------------------------------
// Subsequence fuzzy match (from hts-ui's src/fuzzyMatch.ts)
// ---------------------------------------------------------------------------

/**
 * Subsequence fuzzy match.
 *
 * Returns true when every character of `q` appears in `haystack` in order,
 * case-insensitive. An empty query matches everything.
 *
 * Designed to be cheap enough to run on every keystroke against a few hundred
 * preloaded CommandItem rows. For very large result sets, prefer the
 * `onQueryChange` hook to debounce a server-side search and append results
 * to `items` instead of relying on this filter alone.
 */
export function fuzzyMatch(haystack: string, q: string): boolean { // eslint-disable-line react-refresh/only-export-components
  if (!q) return true;
  const h = haystack.toLowerCase();
  const needle = q.toLowerCase();
  let i = 0;
  for (let j = 0; j < h.length; j++) {
    if (h[j] === needle[i]) i++;
    if (i === needle.length) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Component (from hts-ui's src/CommandPalette.tsx)
// ---------------------------------------------------------------------------

const kbdStyle: CSSProperties = {
  fontSize: 'var(--hts-font-size-xs)',
  fontFamily: 'inherit',
  color: 'var(--text-tertiary)',
  background: 'var(--surface-sunken)',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  padding: '2px 6px',
  lineHeight: 1.4,
};

const DEFAULT_SEARCH_ICON: ReactNode = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const DEFAULT_ENTER_ICON: ReactNode = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="9 10 4 15 9 20" />
    <path d="M20 4v7a4 4 0 0 1-4 4H4" />
  </svg>
);

const DEFAULT_ROW_ICON: ReactNode = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

/**
 * ⌘K-style command palette. Single source of truth for the HTS hubs'
 * power-search UX — see AGENTS.md for the cross-repo model.
 *
 * Theming is entirely via HTS design-token CSS variables (see README).
 * The component ships zero runtime dependencies.
 */
export function CommandPalette({
  open,
  onClose,
  items,
  placeholder = 'Search pages, actions…',
  onQueryChange,
  searchIcon = DEFAULT_SEARCH_ICON,
  enterIcon = DEFAULT_ENTER_ICON,
  renderEmpty,
  zIndex = 2000,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (!q && it.hideWhenEmpty) return false;
      return fuzzyMatch(
        `${it.label} ${it.hint ?? ''} ${it.keywords ?? ''}`.toLowerCase(),
        q,
      );
    });
  }, [items, query]);

  // Reset when opened (and clear any in-flight server-side entity search).
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      onQueryChange?.('');
      const t = window.setTimeout(() => inputRef.current?.focus(), 20);
      return () => window.clearTimeout(t);
    }
  }, [open, onQueryChange]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  // Keep the active row scrolled into view.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const it = results[active];
      if (it) {
        onClose();
        it.perform();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'Tab') {
      // Single-input dialog: trap focus on the search field so Tab can't
      // escape to the page behind the modal.
      e.preventDefault();
      inputRef.current?.focus();
    }
  };

  // Track group headers as we render the flat, already-filtered list.
  let lastGroup: string | undefined;

  return (
    <div
      role="presentation"
      onMouseDown={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '12vh 16px 16px',
        background: 'rgba(8, 12, 20, 0.45)',
        backdropFilter: 'blur(4px)',
        animation: 'fadeIn var(--dur-fast) var(--ease-out)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command menu"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        style={{
          width: 'min(640px, 100%)',
          background: 'var(--surface-overlay)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--hts-radius-lg)',
          boxShadow: 'var(--elev-popover)',
          overflow: 'hidden',
          animation: 'slideUp var(--dur-base) var(--ease-out)',
        }}
      >
        {/* Search row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 16px',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <span style={{ color: 'var(--text-tertiary)', flexShrink: 0, display: 'flex' }}>
            {searchIcon}
          </span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              onQueryChange?.(e.target.value);
            }}
            placeholder={placeholder}
            aria-label="Search commands"
            role="combobox"
            aria-expanded={true}
            aria-controls="cmdk-listbox"
            aria-autocomplete="list"
            aria-activedescendant={
              results[active] ? `cmdk-opt-${active}` : undefined
            }
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 'var(--hts-font-size-base)',
              color: 'var(--text-primary)',
              fontFamily: 'inherit',
            }}
          />
          <kbd style={kbdStyle}>esc</kbd>
        </div>

        {/* Results */}
        <div
          ref={listRef}
          id="cmdk-listbox"
          role="listbox"
          aria-label="Commands"
          style={{ maxHeight: '52vh', overflowY: 'auto', padding: 6 }}
        >
          {results.length === 0 &&
            (renderEmpty ? (
              renderEmpty(query)
            ) : (
              <div
                style={{
                  padding: '28px 16px',
                  textAlign: 'center',
                  color: 'var(--text-tertiary)',
                  fontSize: 'var(--hts-font-size-sm)',
                }}
              >
                No matches for &ldquo;{query}&rdquo;
              </div>
            ))}
          {results.map((it, idx) => {
            const showGroup = it.group && it.group !== lastGroup;
            // hts-ui v0.1.0 intentionally mutates `lastGroup` during render to track
            // group transitions across the flat filtered list. Preserved verbatim so
            // behavior matches the upstream release exactly. See migration note above.
            // eslint-disable-next-line react-hooks/immutability
            lastGroup = it.group;
            const isActive = idx === active;
            return (
              <Fragment key={it.id}>
                {showGroup && (
                  <div
                    style={{
                      padding: '10px 12px 4px',
                      fontSize: 'var(--hts-font-size-xs)',
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    {it.group}
                  </div>
                )}
                <div
                  data-idx={idx}
                  id={`cmdk-opt-${idx}`}
                  role="option"
                  aria-selected={isActive}
                  onMouseMove={() => setActive(idx)}
                  onClick={() => {
                    onClose();
                    it.perform();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '9px 12px',
                    borderRadius: 'var(--hts-radius-md)',
                    cursor: 'pointer',
                    background: isActive ? 'var(--accent-soft)' : 'transparent',
                    color: 'var(--text-primary)',
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      color: isActive
                        ? 'var(--text-accent)'
                        : 'var(--text-tertiary)',
                      flexShrink: 0,
                    }}
                  >
                    {it.icon ?? DEFAULT_ROW_ICON}
                  </span>
                  <span
                    style={{ flex: 1, fontSize: 'var(--hts-font-size-sm)' }}
                  >
                    {it.label}
                  </span>
                  {it.hint && (
                    <span
                      style={{
                        fontSize: 'var(--hts-font-size-xs)',
                        color: 'var(--text-tertiary)',
                      }}
                    >
                      {it.hint}
                    </span>
                  )}
                  {isActive && (
                    <span style={{ color: 'var(--text-tertiary)', display: 'flex' }}>
                      {enterIcon}
                    </span>
                  )}
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
